// A drill-in's params — the `params` of a card action's `nav: { page, params }`, which the runtime posts as
// `{kind:'navigate', destination:{page, params}}` and the opened page reads back with `sym.params()`
// (`{kind:'params', id}`). The host carries them through the route as ONE query key holding JSON
// (`/pages/x-detail?params=%7B%22bookingId%22%3A%22b-1%22%7D`), never in the iframe's render URL: the render
// document is HTTP-cached by its URL, and a per-booking URL would make every drill-in a cold render.
//
// The same rules as the runtime's shim, applied on BOTH ends (when the host writes the route and when it
// answers the page), because the query string is reader-editable and a pasted link is as much input as a
// widget's message: a plain object of scalars, no `widgetSlug` (the shim reserves it), nothing over 4 KB.
// Nothing here throws — a bad value is no params, and a page with no params shows its list.

export type NavParams = Record<string, string | number | boolean>;

/** The parent route's query key. */
export const NAV_PARAMS_QUERY_KEY = 'params';

/** The JSON size past which params are dropped whole, matching the runtime shim's 4 KB cap. */
export const MAX_NAV_PARAMS_CHARS = 4096;

const RESERVED_KEYS = new Set(['widgetSlug']);

/** Keeps only the scalar entries of a plain object; anything else is `{}`. */
export function sanitizeNavParams(value: unknown): NavParams {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return {};
  const out: NavParams = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (RESERVED_KEYS.has(key)) continue;
    // A non-finite number is not JSON (it serializes as null), so it is not a scalar the page can read back.
    if (typeof v === 'string' || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v))) out[key] = v;
  }
  return out;
}

/** The route's raw `params` query value → sanitized params. A repeated key, garbage or an oversized value is `{}`. */
export function parseNavParams(raw: unknown): NavParams {
  if (typeof raw !== 'string' || !raw || raw.length > MAX_NAV_PARAMS_CHARS) return {};
  try {
    return sanitizeNavParams(JSON.parse(raw));
  } catch {
    return {};
  }
}

/** Params → the route's `params` value, or `undefined` when there are none (or too many), so the key is omitted. */
export function encodeNavParams(value: unknown): string | undefined {
  const params = sanitizeNavParams(value);
  if (Object.keys(params).length === 0) return undefined;
  const json = JSON.stringify(params);
  return json.length > MAX_NAV_PARAMS_CHARS ? undefined : json;
}

/**
 * The pieces a navigate adds to a drill-in's route: the `query` entry for the Next href and the `search`
 * suffix for its `as` path. Both are empty when there are no params, so a navigation without params produces
 * exactly the URL it did before params existed.
 */
export function navParamsRoute(value: unknown): { query: { [NAV_PARAMS_QUERY_KEY]?: string }; search: string } {
  const json = encodeNavParams(value);
  if (json === undefined) return { query: {}, search: '' };
  return { query: { [NAV_PARAMS_QUERY_KEY]: json }, search: `?${NAV_PARAMS_QUERY_KEY}=${encodeURIComponent(json)}` };
}
